import { useCallback, useState, type FunctionComponent } from 'react'
import { InternalLink } from '../../const/Links'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import { CheckboxRegular } from '../form/CheckboxRegular'
import { Button } from './Button'
import { Collapsible } from './Collapsible'
import style from './CookieBanner.module.sass'
import {
	useAcceptCookies,
	useDeclineCookies,
	useHandleNonNecessaryCookies,
	useIsCookieBarVisible,
} from './CookieProvider'

export const CookieBanner: FunctionComponent = () => {
	const translate = useTranslate()
	const isVisible = useIsCookieBarVisible()
	const handleNonNecessaryCookies = useHandleNonNecessaryCookies()
	const [isSettingsOpen, setIsSettingsOpen] = useState(false)

	const accept = useAcceptCookies()
	const decline = useDeclineCookies()

	const [isMarketingCookies, setIsMarketingCookies] = useState(false)
	const [isAnalyticalCookies, setIsAnalyticalCookies] = useState(false)

	const onHandleSettings = useCallback((visible: boolean) => {
		setIsSettingsOpen(visible)
	}, [])

	const onSave = useCallback(() => {
		handleNonNecessaryCookies(isMarketingCookies, isAnalyticalCookies)
	}, [handleNonNecessaryCookies, isMarketingCookies, isAnalyticalCookies])

	return (
		<>
			{isVisible && (
				<div className={style.root} data-nosnippet>
					<Collapsible open={isSettingsOpen} hasHighIndex>
						<div className={style.settingsText}>
							<Translation tr="cookieBanner.text" />
							<div className={style.checkboxGroup}>
								<div className={style.checkboxWrapper}>
									<CheckboxRegular
										name="technicalCookies"
										label={translate('cookieBanner.checkbox.technicalCookies')}
										checked
										disabled
									/>
								</div>
								<div className={style.checkboxInfo}>
									<Translation tr="cookieBanner.info.technical.cookies" />
								</div>
								<div className={style.checkboxWrapper}>
									<CheckboxRegular
										name="analyticsCookies"
										label={translate('cookieBanner.checkbox.analyticalCookies')}
										checked={isAnalyticalCookies}
										onChange={(event) => {
											setIsAnalyticalCookies(event.target.checked)
										}}
									/>
								</div>
								<div className={style.checkboxInfo}>
									<Translation tr="cookieBanner.info.analytical.cookies" />
								</div>
								<div className={style.checkboxWrapper}>
									<CheckboxRegular
										name="marketingCookies"
										label={translate('cookieBanner.checkbox.marketingCookies')}
										checked={isMarketingCookies}
										onChange={(event) => {
											setIsMarketingCookies(event.target.checked)
										}}
									/>
								</div>
								<div className={style.checkboxInfo}>
									<Translation tr="cookieBanner.info.marketing.cookies" />
								</div>
							</div>
						</div>
					</Collapsible>
					<Collapsible open={!isSettingsOpen}>
						<p className={style.text}>
							<Translation tr="cookieBanner.text.toFind" />{' '}
							<Button
								url={InternalLink.gdpr}
								label={translate('cookieBanner.button.cookies')}
								type={{ variant: 'underlined' }}
							/>
							.
							<br /> <Translation tr="cookieBanner.text.agree" /> {/* TODO: cookies adresa */}
						</p>
					</Collapsible>
					<div className={style.buttonGroup}>
						<div className={style.buttonWrapperSettings}>
							{!isSettingsOpen && (
								<Button
									label={translate('cookieBanner.button.settings')}
									type={{ variant: 'underlined' }}
									onClick={() => onHandleSettings(true)}
								/>
							)}
							{isSettingsOpen && (
								<Button
									label={translate('cookieBanner.button.back')}
									type={{ variant: 'underlined' }}
									onClick={() => onHandleSettings(false)}
								/>
							)}
						</div>
						{!isSettingsOpen && (
							<div className={style.buttonWrapperNo}>
								<Button
									label={translate('cookieBanner.button.no')}
									type={{ variant: 'bordered' }}
									onClick={decline}
									noShrink
								/>
							</div>
						)}
						<div className={style.buttonWrapperYes}>
							{isSettingsOpen && <Button label={translate('cookieBanner.button.save')} onClick={onSave} noShrink />}
							{!isSettingsOpen && <Button label={translate('cookieBanner.button.yes')} onClick={accept} noShrink />}
						</div>
					</div>
				</div>
			)}
		</>
	)
}
